import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Link as RouterLink, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import type {
  StrapiCustomerRow,
  StrapiQuotationCreateBundleRequest,
  StrapiQuotationCreateData,
  StrapiQuotationRow,
  StrapiQuotationUpdateData,
  StrapiQuotationUserStub,
} from '../../../api/strapiCrm';
import {
  strapiCreateQuotationOtherCost,
  strapiCreateQuotationSampleInformation,
  strapiCreateQuotationGroup,
  strapiCreateSampleInformation,
  strapiDeleteQuotationGroup,
  strapiDeleteQuotationOtherCost,
  strapiDeleteQuotationSampleInformation,
  strapiDeleteSampleInformation,
  strapiUpdateQuotationGroup,
  strapiUpdateQuotationOtherCost,
  strapiUpdateQuotationSampleInformation,
  strapiUpdateSampleInformation,
  strapiSyncQuotationGroupRecurringEvents,
} from '../../../api/strapiCrm';
import type { StrapiManagedUserRow } from '../../../api/strapiUsers';
import { strapiUserRelationConnect } from '../../../api/strapiUsers';
import { useAuth } from '../../../auth/AuthContext';
import {
  canCopyQuotation,
  canCreateQuotation,
  canPicEditQuotation,
  canShowPicApproveRevision,
  canShowSubmitOnQuotationEdit,
  isAdminKeuangan,
  isMarketingPic,
  quotationEditRequestStatus,
  quotationHasApprovedEditRequest,
  quotationIsViewOnly,
  resolveQuotationWorkflowUser,
  quotationWorkflowStatus,
  quotationIsReturnedToPicStatus,
} from '../quotationWorkflow';
import { QuotationWorkflowActions } from '../QuotationWorkflowActions';
import { appDocumentTitle } from '../../../appMeta';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreateQuotationMutation,
  useCopyQuotationMutation,
  useNavigationMenuQuery,
  useQuotationDetailQuery,
  useUpdateQuotationMutation,
  useUsersQuery,
} from '../../../query/hooks';
import { queryKeys } from '../../../query/queryKeys';
import { PAGE_SHELL_SCROLLABLE_TABS_SX, PageShell } from '../../../layout/PageShell';
import { CollapsibleKeteranganSplit } from '../../../ui/CollapsibleKeteranganSplit';
import { QUOTATION_STATUS_OPTIONS } from './quotationFormConstants';
import { createQuotationFormSchema } from './quotationFormSchema';
import { quotationDateInputFromApi, quotationDateInputToday, quotationDatetimeLocalFromIso, quotationIsoFromDatetimeLocal } from './quotationFormDateTime';
import { quotationContractEndDateLocalFromSources } from './quotationRecurring';
import {
  emptyQuotationFormGroupRow,
  patchQuotationContractFlags,
  quotationFormFinancialBreakdown,
  quotationFormFlatSamplesFromDetailLines,
  quotationFormGroupsFromDetailLines,
  quotationGroupApiDataFromSavePayload,
  quotationUsesSampleGroups,
  validateQuotationFormGroupsForSave,
  type QuotationFormGroupRow,
} from './quotationFormGroups';
import type { QuotationFormValues } from './quotationFormTypes';
import { QuotationFormDetailTabScreen } from './TabScreens/Detail';
import { QuotationFormCatatanQuotationTabScreen } from './TabScreens/CatatanQuotation';
import {
  computeEffectiveDiscountRupiah,
  quotationFormOtherCostsFromDetailLines,
  quotationFormSubtotalBeforeDiscount,
  validateQuotationFormOtherCostsForSave,
} from './TabScreens/BiayaLainnya';
import { QuotationLogsTabScreen } from '../TabScreens/QuotationLogs';
import { resolveSamplingSchedulePath } from '../../SamplingSchedulesPage/resolveSamplingSchedulePath';
import { QuotationFormSampleGroupTabScreen } from './TabScreens/SampleGroup';
import {
  quotationFormSamplesFromDetailLines,
  QuotationFormSamplesTabScreen,
  validateQuotationFormSamplesForSave,
} from './TabScreens/Samples';

export { QUOTATION_STATUS_OPTIONS };

type QuotationFormTabKey = 'detail' | 'samples' | `group-${number}` | 'notes' | 'logs';

function groupTabKey(index: number): `group-${number}` {
  return `group-${index}`;
}

function parseGroupTabIndex(tab: QuotationFormTabKey): number | null {
  const m = /^group-(\d+)$/.exec(tab);
  return m ? Number.parseInt(m[1], 10) : null;
}

function buildQuotationFormTabHash(tab: QuotationFormTabKey): string {
  if (tab === 'samples') return '#sampel';
  if (tab === 'notes') return '#catatan-quotation';
  if (tab === 'logs') return '#quotation-logs';
  const groupIndex = parseGroupTabIndex(tab);
  if (groupIndex != null) return `#sampel-${groupIndex + 1}`;
  return '#detail';
}

function parseQuotationFormTabHash(hash: string): QuotationFormTabKey | null {
  const raw = (hash || '').replace(/^#/, '').trim().toLowerCase();
  if (!raw) return null;
  if (raw === 'detail') return 'detail';
  if (raw === 'sampel' || raw === 'samples') return 'samples';
  if (raw === 'catatan-quotation' || raw === 'notes' || raw === 'catatan') return 'notes';
  if (raw === 'quotation-logs' || raw === 'logs') return 'logs';
  const match = /^sampel-(\d+)$/.exec(raw);
  if (!match) return null;
  const n = Number.parseInt(match[1], 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return groupTabKey(n - 1);
}

function resolveQuotationFormTab(
  tab: QuotationFormTabKey,
  ctx: {
    grouped: boolean;
    groupCount: number;
    showNotes: boolean;
    showLogs: boolean;
  },
): QuotationFormTabKey {
  if (tab === 'notes') return ctx.showNotes ? 'notes' : 'detail';
  if (tab === 'logs') return ctx.showLogs ? 'logs' : 'detail';
  const groupIndex = parseGroupTabIndex(tab);
  if (groupIndex != null) {
    if (!ctx.grouped || ctx.groupCount < 1) return 'detail';
    if (groupIndex >= ctx.groupCount) return groupTabKey(ctx.groupCount - 1);
    return tab;
  }
  if (tab === 'samples') {
    if (!ctx.grouped) return 'samples';
    return ctx.groupCount > 0 ? groupTabKey(0) : 'detail';
  }
  return 'detail';
}

type QuotationSuccessCreate = { quotation_number: string };

function managedUserFromQuotationStub(u: StrapiQuotationUserStub | null | undefined): StrapiManagedUserRow | null {
  if (!u) return null;
  const documentId = u.documentId?.trim();
  const numericId = u.id ?? null;
  if (!documentId && numericId == null && !u.email?.trim() && !u.username?.trim()) return null;
  const resolvedDocId = documentId || (numericId != null ? String(numericId) : '');
  return {
    id: resolvedDocId,
    documentId: resolvedDocId,
    numericId,
    username: u.username,
    email: u.email,
    blocked: false,
    userTypeCode: null,
    userTypeName: null,
  };
}

function managedUserFromSessionUser(
  u: { id?: number; documentId?: string | null; username?: string; email?: string; blocked?: boolean; userTypeCode?: string | null; userTypeName?: string | null } | null | undefined,
): StrapiManagedUserRow | null {
  if (!u) return null;
  const documentId = u.documentId?.trim() || null;
  const numericId = typeof u.id === 'number' && Number.isFinite(u.id) ? u.id : null;
  if (!documentId && numericId == null && !u.email?.trim() && !u.username?.trim()) return null;
  const resolvedId = documentId || (numericId != null ? String(numericId) : (u.email?.trim() || u.username?.trim() || ''));
  if (!resolvedId) return null;
  return {
    id: resolvedId,
    documentId,
    numericId,
    username: u.username ?? '',
    email: u.email ?? '',
    blocked: Boolean(u.blocked),
    userTypeCode: u.userTypeCode ?? null,
    userTypeName: u.userTypeName ?? null,
  };
}

function emptyQuotationFormValues(): QuotationFormValues {
  return {
    quotation_number: '',
    quotation_date: quotationDateInputToday(),
    company_branch: null,
    preorder: false,
    progress_status: 'draft',
    is_contract: false,
    is_consultant_quote: false,
    contract_period_start_date: '',
    contract_period_end_date: '',
    sample_pickup_method: 'sampling',
    assignee_user: null,
    approved_by_user: null,
    discount_amount: '0',
    discount_is_percentage: false,
    discount_percentage: '0',
    is_ppn: false,
    term_of_payment: false,
    term_of_payment_date: '',
    term_of_payment_note: '',
    otherCostRows: [],
    customer: null,
    sampleRows: [],
    sampleGroups: [],
  };
}

function customerStubFromNested(
  c: NonNullable<StrapiQuotationRow['customer']>,
): StrapiCustomerRow {
  return {
    documentId: c.documentId,
    customer_number: c.customer_number,
    name: c.name,
  };
}

function applyZodIssueToTabs(
  issuePath: PropertyKey[],
  message: string,
): {
  detailPatch: {
    customer?: string;
    quotation_number?: string;
    term_of_payment_date?: string;
    contract_period_start_date?: string;
    contract_period_end_date?: string;
  };
  tab: QuotationFormTabKey;
  samplesMessage?: string;
  companyBranchMessage?: string;
  groupIndex?: number;
} {
  const head = issuePath[0];
  if (head === 'customer') {
    return { detailPatch: { customer: message }, tab: 'detail' };
  }
  if (head === 'company_branch') {
    return { detailPatch: {}, tab: 'detail', companyBranchMessage: message };
  }
  if (head === 'quotation_number') {
    return { detailPatch: { quotation_number: message }, tab: 'detail' };
  }
  if (
    head === 'term_of_payment_date' ||
    head === 'term_of_payment_note' ||
    head === 'term_of_payment'
  ) {
    return {
      detailPatch: head === 'term_of_payment_date' ? { term_of_payment_date: message } : {},
      tab: 'detail',
    };
  }
  if (head === 'contract_period_start_date') {
    return { detailPatch: { contract_period_start_date: message }, tab: 'detail' };
  }
  if (head === 'contract_period_end_date') {
    return { detailPatch: { contract_period_end_date: message }, tab: 'detail' };
  }
  if (head === 'sampleGroups') {
    const idx = typeof issuePath[1] === 'number' ? issuePath[1] : 0;
    return { detailPatch: {}, tab: groupTabKey(idx), samplesMessage: message, groupIndex: idx };
  }
  if (
    head === 'sampleRows' ||
    head === 'otherCostRows' ||
    head === 'discount_amount' ||
    head === 'discount_percentage' ||
    head === 'discount_is_percentage'
  ) {
    return { detailPatch: {}, tab: 'samples', samplesMessage: message };
  }
  return { detailPatch: {}, tab: 'detail' };
}

/**
 * Routes: `/quotations/new` (create), `/quotation/:documentId/edit` (edit), `/quotation/:documentId/view` (read-only).
 * `quotation_number` is assigned on create (server-side); GL job numbers belong on work orders only.
 */
type SaveIntent = 'draft' | 'submit';

export function QuotationFormRoute() {
  const { jwt, user, loading: authLoading } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const { documentId: rawDocumentId } = useParams<{ documentId: string }>();

  const pathNorm = pathname.replace(/\/$/, '');
  const isCreate = pathNorm === '/quotations/new';
  const isEdit = /^\/quotation\/[^/]+\/edit$/.test(pathNorm);
  const isView = /^\/quotation\/[^/]+\/view$/.test(pathNorm);
  const detailId =
    (isEdit || isView) && rawDocumentId ? decodeURIComponent(rawDocumentId) : null;

  const detailQ = useQuotationDetailQuery(jwt, detailId);
  const menuQ = useNavigationMenuQuery(jwt);
  const createMut = useCreateQuotationMutation(jwt);
  const copyMut = useCopyQuotationMutation(jwt);
  const updateMut = useUpdateQuotationMutation(jwt);
  const selfUserQ = useUsersQuery(jwt, {
    emailContains: user?.email?.trim() ?? '',
    status: 'active',
  });

  const quotationFormSchema = useMemo(() => createQuotationFormSchema(isCreate), [isCreate]);

  const workflowUser = useMemo(
    () => resolveQuotationWorkflowUser(user, selfUserQ.data?.rows),
    [user, selfUserQ.data?.rows],
  );

  const canEditForm = useMemo(() => {
    if (isView) return false;
    if (isAdminKeuangan(workflowUser) || isAdminKeuangan(user)) return false;
    if (isCreate) return canCreateQuotation(workflowUser ?? user);
    if (!detailQ.data) return false;
    if (quotationIsViewOnly(detailQ.data)) return false;
    if (authLoading) {
      const s = quotationWorkflowStatus(detailQ.data);
      return (
        s === 'edit_requested' ||
        s === 'need_attention' ||
        s === 'draft' ||
        quotationIsReturnedToPicStatus(s) ||
        quotationHasApprovedEditRequest(detailQ.data)
      );
    }
    return canPicEditQuotation(detailQ.data, workflowUser);
  }, [isView, isCreate, detailQ.data, workflowUser, user, authLoading]);

  const showSubmitOnEdit = useMemo(
    () => isEdit && canShowSubmitOnQuotationEdit(detailQ.data, canEditForm),
    [isEdit, detailQ.data, canEditForm],
  );

  const formReadOnly = isView || !canEditForm;

  const [values, setValues] = useState<QuotationFormValues>(() => emptyQuotationFormValues());

  const patchValues = useCallback((patch: Partial<QuotationFormValues>) => {
    setValues((v) => ({ ...v, ...patch }));
    setDetailErrors((e) => {
      const next = { ...e };
      if ('customer' in patch) delete next.customer;
      if ('quotation_number' in patch) delete next.quotation_number;
      if ('company_branch' in patch) delete next.company_branch;
      if ('term_of_payment_date' in patch) delete next.term_of_payment_date;
      if ('contract_period_start_date' in patch) delete next.contract_period_start_date;
      if ('contract_period_end_date' in patch) delete next.contract_period_end_date;
      return next;
    });
  }, []);

  const patchContractFlags = useCallback(
    (patch: Partial<Pick<QuotationFormValues, 'is_contract' | 'is_consultant_quote'>>) => {
      setValues((v) => {
        const next = { ...v, ...patchQuotationContractFlags(v, patch) };
        if (!quotationUsesSampleGroups(next)) {
          setTab((current) => {
            if (parseGroupTabIndex(current) == null) return current;
            return next.sampleRows.length > 0 ? 'samples' : 'detail';
          });
        } else if (!quotationUsesSampleGroups(v) && next.sampleGroups.length > 0) {
          setTab(groupTabKey(0));
        }
        return next;
      });
      if (patch.is_contract === false) {
        setDetailErrors((e) => {
          const next = { ...e };
          delete next.contract_period_start_date;
          delete next.contract_period_end_date;
          return next;
        });
      }
    },
    [],
  );

  const quotationStatus = detailQ.data?.progress_status ?? values.progress_status;
  const isDraftQuotation = String(quotationStatus ?? 'draft').trim() === 'draft';

  const showLogsTab = useMemo(() => {
    if (!(isEdit || isView) || !detailId) return false;
    if (!isDraftQuotation) return true;
    return menuQ.data?.access_codes?.includes('can_view_quotation_log') ?? true;
  }, [isEdit, isView, detailId, isDraftQuotation, menuQ.data?.access_codes]);

  const showNotesTab = useMemo(() => {
    if (!(isEdit || isView)) return false;
    const hasEdit = Boolean(detailQ.data?.remark_edit?.trim());
    const hasEditReview = Boolean(detailQ.data?.remark_edit_review?.trim());
    const hasApproval = Boolean(detailQ.data?.remark_approval?.trim());
    const ers = detailQ.data ? quotationEditRequestStatus(detailQ.data) : 'none';
    if (!isDraftQuotation) return true;
    const st = String(quotationStatus ?? '').trim();
    if (
      st !== 'edit_requested' &&
      st !== 'need_attention' &&
      st !== 'approved' &&
      st !== 'rejected' &&
      st !== 'rejected_by_manajer' &&
      ers === 'none'
    ) {
      return false;
    }
    return hasEdit || hasEditReview || hasApproval;
  }, [
    isEdit,
    isView,
    isDraftQuotation,
    quotationStatus,
    detailQ.data,
  ]);

  const [tab, setTab] = useState<QuotationFormTabKey>(
    () => parseQuotationFormTabHash(location.hash) ?? 'detail',
  );
  const [formHydrated, setFormHydrated] = useState(isCreate);
  const [successCreate, setSuccessCreate] = useState<QuotationSuccessCreate | null>(null);
  const [successUpdated, setSuccessUpdated] = useState(false);
  const [samplesSaveError, setSamplesSaveError] = useState<string | null>(null);
  const [postCreateSamplesError, setPostCreateSamplesError] = useState<string | null>(null);
  const [postCreateOtherCostsError, setPostCreateOtherCostsError] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [detailErrors, setDetailErrors] = useState<{
    customer?: string;
    quotation_number?: string;
    company_branch?: string;
    term_of_payment_date?: string;
    contract_period_start_date?: string;
    contract_period_end_date?: string;
  }>({});

  const customer = values.customer;
  const quotation_number = values.quotation_number;

  const pageHeading = isView ? 'Detail quotation' : isCreate ? 'Quotation baru' : 'Ubah quotation';
  useDocumentTitle(appDocumentTitle(pageHeading));

  useEffect(() => {
    setTab(parseQuotationFormTabHash(window.location.hash) ?? 'detail');
  }, [isCreate, isView, detailId]);

  useEffect(() => {
    if (tab === 'logs' && !showLogsTab) setTab('detail');
  }, [tab, showLogsTab]);

  useEffect(() => {
    if (tab === 'notes' && !showNotesTab) setTab('detail');
  }, [tab, showNotesTab]);

  const quotationSamplesHydrateKey = useMemo(() => {
    if (!detailQ.data?.documentId) return '';
    const xs = detailQ.data.sample_informations ?? [];
    const costs = detailQ.data.quotation_other_costs ?? [];
    const groups = detailQ.data.quotation_groups ?? [];
    return `${detailQ.data.documentId}:${xs
      .map((s) => `${s.documentId}:${s.quotation_group?.documentId ?? ''}`)
      .join(',')}:costs:${costs.map((c) => `${c.documentId}:${c.quotation_group?.documentId ?? ''}`).join(',')}:groups:${groups
      .map((g) => g.documentId)
      .join(',')}`;
  }, [detailQ.data]);

  const quotationOtherCostsHydrateKey = useMemo(() => {
    if (!detailQ.data?.documentId) return '';
    const xs = detailQ.data.quotation_other_costs ?? [];
    return `${detailQ.data.documentId}:costs:${xs.map((s) => s.documentId).join(',')}`;
  }, [detailQ.data]);

  useEffect(() => {
    setPostCreateOtherCostsError(null);
    if (isCreate) return;
    if (!detailQ.data?.documentId || !detailId || !detailQ.data) return;
    const row = detailQ.data;
    const usesGroups = row.is_contract || row.is_consultant_quote;
    if (usesGroups) return;
    const list = row.quotation_other_costs ?? [];
    const nextCosts = list.length ? quotationFormOtherCostsFromDetailLines(list) : [];
    setValues((v) => ({ ...v, otherCostRows: nextCosts }));
  }, [isCreate, detailId, quotationOtherCostsHydrateKey, detailQ.data?.documentId]);

  useEffect(() => {
    setSamplesSaveError(null);
    setPostCreateSamplesError(null);
    if (isCreate) return;
    if (!detailQ.data?.documentId || !detailId || !detailQ.data) return;
    const row = detailQ.data;
    const list = row.sample_informations ?? [];
    const allOtherCosts = row.quotation_other_costs ?? [];
    const usesGroups = row.is_contract || row.is_consultant_quote;
    if (usesGroups) {
      const nextGroups = quotationFormGroupsFromDetailLines(
        list,
        allOtherCosts,
        row.quotation_groups ?? [],
      );
      setValues((v) => ({ ...v, sampleGroups: nextGroups, sampleRows: [], otherCostRows: [] }));
    } else {
      const nextSamples = list.length ? quotationFormFlatSamplesFromDetailLines(list) : [];
      setValues((v) => ({ ...v, sampleRows: nextSamples, sampleGroups: [] }));
    }
  }, [isCreate, detailId, quotationSamplesHydrateKey, detailQ.data?.documentId]);

  const usesGroupedSamples = quotationUsesSampleGroups(values);

  const financialBreakdown = useMemo(
    () =>
      quotationFormFinancialBreakdown({
        is_contract: values.is_contract,
        is_consultant_quote: values.is_consultant_quote,
        contract_period_start_date: values.contract_period_start_date,
        contract_period_end_date: values.contract_period_end_date,
        sampleGroups: values.sampleGroups,
        sampleRows: values.sampleRows,
        otherCostRows: values.otherCostRows,
      }),
    [
      values.is_contract,
      values.is_consultant_quote,
      values.contract_period_start_date,
      values.contract_period_end_date,
      values.sampleGroups,
      values.sampleRows,
    ],
  );

  const handleAddSampleGroup = useCallback(() => {
    setValues((v) => {
      const nextGroups = [...v.sampleGroups, emptyQuotationFormGroupRow()];
      setTab(groupTabKey(nextGroups.length - 1));
      return { ...v, sampleGroups: nextGroups };
    });
  }, []);

  const handleRemoveSampleGroup = useCallback((index: number) => {
    setValues((v) => {
      const prevLen = v.sampleGroups.length;
      const nextGroups = v.sampleGroups.filter((_, i) => i !== index);
      setTab((current) => {
        const gi = parseGroupTabIndex(current);
        if (gi == null) return current;
        if (gi === index) {
          if (nextGroups.length === 0) return 'detail';
          if (index >= prevLen - 1) return groupTabKey(nextGroups.length - 1);
          return groupTabKey(index);
        }
        if (gi > index) return groupTabKey(gi - 1);
        return current;
      });
      return { ...v, sampleGroups: nextGroups };
    });
  }, []);

  useEffect(() => {
    if (!isCreate) return;
    setFormHydrated(true);
    setSamplesSaveError(null);
    setPostCreateSamplesError(null);
    setPostCreateOtherCostsError(null);
    setDetailErrors({});
    setSuccessCreate(null);
    setValues(emptyQuotationFormValues());
  }, [isCreate]);

  useEffect(() => {
    if (isCreate) return;
    setSamplesSaveError(null);
    setPostCreateSamplesError(null);
    setPostCreateOtherCostsError(null);
    setDetailErrors({});
    const row = detailQ.data;
    if (!row) {
      setFormHydrated(false);
      return;
    }
    setValues({
      quotation_number: row.quotation_number ?? '',
      quotation_date: quotationDateInputFromApi(row.quotation_date),
      company_branch: row.company_branch ?? null,
      preorder: Boolean(row.preorder),
      progress_status: (row.progress_status as QuotationFormValues['progress_status']) ?? 'draft',
      is_contract: Boolean(row.is_contract),
      is_consultant_quote: Boolean(row.is_consultant_quote),
      contract_period_start_date: quotationDatetimeLocalFromIso(row.contract_period_start_date),
      contract_period_end_date: quotationContractEndDateLocalFromSources({
        contract_period_end_date: row.contract_period_end_date,
        contract_period_start_date: row.contract_period_start_date,
        contract_period_long: row.contract_period_long,
        contract_period_type: row.contract_period_type,
      }),
      sample_pickup_method: row.sample_pickup_method ?? 'sampling',
      assignee_user: managedUserFromQuotationStub(row.assignee_user ?? undefined),
      approved_by_user: managedUserFromQuotationStub(row.approved_by_user ?? undefined),
      discount_amount: String(row.discount_amount ?? 0),
      discount_is_percentage: false,
      discount_percentage: '0',
      is_ppn: Boolean(row.is_ppn),
      term_of_payment: Boolean(row.term_of_payment),
      term_of_payment_date: quotationDatetimeLocalFromIso(row.term_of_payment_date),
      term_of_payment_note: row.term_of_payment_note?.trim() ?? '',
      customer: row.customer ? customerStubFromNested(row.customer) : null,
      sampleGroups:
        row.is_contract || row.is_consultant_quote
          ? quotationFormGroupsFromDetailLines(
              row.sample_informations ?? [],
              row.quotation_other_costs ?? [],
              row.quotation_groups ?? [],
            )
          : [],
      sampleRows:
        row.is_contract || row.is_consultant_quote
          ? []
          : (row.sample_informations ?? []).length
            ? quotationFormFlatSamplesFromDetailLines(row.sample_informations ?? [])
            : [],
      otherCostRows:
        row.is_contract || row.is_consultant_quote
          ? []
          : (row.quotation_other_costs ?? []).length
            ? quotationFormOtherCostsFromDetailLines(row.quotation_other_costs ?? [])
            : [],
    });
    setFormHydrated(true);
  }, [isCreate, detailQ.data]);

  const quotationTabContext = useMemo(
    () => ({
      grouped: usesGroupedSamples,
      groupCount: values.sampleGroups.length,
      showNotes: showNotesTab,
      showLogs: showLogsTab,
    }),
    [usesGroupedSamples, values.sampleGroups.length, showNotesTab, showLogsTab],
  );

  const resolvedTab = formHydrated
    ? resolveQuotationFormTab(tab, quotationTabContext)
    : parseGroupTabIndex(tab) == null &&
        (tab !== 'notes' || showNotesTab) &&
        (tab !== 'logs' || showLogsTab)
      ? tab
      : 'detail';

  useEffect(() => {
    const parsed = parseQuotationFormTabHash(location.hash);
    if (!parsed) return;
    setTab((current) => (current === parsed ? current : parsed));
  }, [location.hash]);

  useEffect(() => {
    if (!formHydrated) {
      if (window.location.hash && window.location.hash !== '#') return;
      if (window.location.hash === '#detail') return;
      navigate(
        { pathname: location.pathname, search: location.search, hash: '#detail' },
        { replace: true },
      );
      return;
    }

    const resolved = resolveQuotationFormTab(tab, quotationTabContext);
    const expected = buildQuotationFormTabHash(resolved);
    if (window.location.hash !== expected) {
      const currentParsed = parseQuotationFormTabHash(window.location.hash);
      const currentResolved = currentParsed
        ? resolveQuotationFormTab(currentParsed, quotationTabContext)
        : null;
      const replace = currentResolved == null || currentResolved === resolved;
      navigate(
        { pathname: location.pathname, search: location.search, hash: expected },
        { replace },
      );
    }
    if (resolved !== tab) setTab(resolved);
  }, [
    formHydrated,
    tab,
    quotationTabContext,
    navigate,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    if (!isCreate) return;
    const email = user?.email?.trim().toLowerCase();
    const fromList = email
      ? (selfUserQ.data?.rows ?? []).find((u) => u.email?.trim().toLowerCase() === email)
      : undefined;
    const me =
      fromList ??
      managedUserFromSessionUser(resolveQuotationWorkflowUser(user, selfUserQ.data?.rows));
    if (!me) return;
    setValues((v) => {
      if (!v.assignee_user) return { ...v, assignee_user: me };
      const sameEmail =
        Boolean(v.assignee_user.email?.trim()) &&
        v.assignee_user.email.trim().toLowerCase() === (me.email?.trim().toLowerCase() || '');
      if (sameEmail && !v.assignee_user.documentId && me.documentId) {
        return { ...v, assignee_user: me };
      }
      return v;
    });
  }, [isCreate, user, selfUserQ.data?.rows]);

  const saveDisabled = useMemo(() => {
    if (formReadOnly) return true;
    if (isCreate) {
      return createMut.isPending || !customer;
    }
    return updateMut.isPending || !quotation_number.trim() || !customer;
  }, [formReadOnly, createMut.isPending, updateMut.isPending, quotation_number, customer, isCreate]);

  const savePending = createMut.isPending || updateMut.isPending;

  const handleTabsChange = useCallback(
    (_: React.SyntheticEvent, nextTab: QuotationFormTabKey) => {
      if (!isView && (nextTab === 'samples' || parseGroupTabIndex(nextTab) != null)) {
        if (!values.customer?.documentId?.trim()) {
          setDetailErrors((e) => ({ ...e, customer: 'Pelanggan wajib dipilih.' }));
          setTab('detail');
          return;
        }
        if (!isCreate && !values.quotation_number.trim()) {
          setDetailErrors((e) => ({ ...e, quotation_number: 'Nomor quotation wajib diisi.' }));
          setTab('detail');
          return;
        }
      }
      setTab(nextTab);
    },
    [values.customer, values.quotation_number, isCreate, isView],
  );

  async function submitValidated(saveIntent: SaveIntent = 'draft'): Promise<void> {
    if (!jwt || formReadOnly) return;
    setSamplesSaveError(null);

    const valuesForSave = {
      ...values,
      progress_status: (isCreate
        ? saveIntent === 'submit'
          ? 'submitted'
          : 'draft'
        : values.progress_status) as QuotationFormValues['progress_status'],
    };
    const parsed = quotationFormSchema.safeParse(valuesForSave);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const msg = issue?.message ?? 'Periksa formulir.';
      const { detailPatch, tab: errTab, samplesMessage, companyBranchMessage } = applyZodIssueToTabs(
        issue?.path ?? [],
        msg,
      );
      setDetailErrors((e) => ({
        ...e,
        ...detailPatch,
        ...(companyBranchMessage ? { company_branch: companyBranchMessage } : {}),
      }));
      setTab(errTab);
      if (samplesMessage) setSamplesSaveError(samplesMessage);
      return;
    }

    const data = parsed.data;
    const {
      company_branch: company_branch_row,
      preorder,
      progress_status,
      customer: cust,
      quotation_number: qNum,
      quotation_date,
      sampleRows,
      sampleGroups,
      is_contract,
      is_consultant_quote,
      contract_period_start_date,
      contract_period_end_date,
      sample_pickup_method,
      discount_amount: discountStr,
      discount_is_percentage,
      discount_percentage,
      is_ppn,
      term_of_payment,
      term_of_payment_date,
      term_of_payment_note,
    } = data;
    const assignee_u = data.assignee_user as StrapiManagedUserRow | null;
    const approver_u = data.approved_by_user as StrapiManagedUserRow | null;
    if (!cust?.documentId) return;

    const groupedMode = is_contract || is_consultant_quote;
    const allOtherCostRows = groupedMode
      ? sampleGroups.flatMap((g) => g.otherCostRows)
      : data.otherCostRows;
    const financialSamples = quotationFormFinancialBreakdown({
      is_contract,
      is_consultant_quote,
      contract_period_start_date,
      contract_period_end_date,
      sampleGroups,
      sampleRows,
      otherCostRows: data.otherCostRows,
    });

    const subtotalBeforeDiscount = quotationFormSubtotalBeforeDiscount(
      financialSamples.rows,
      allOtherCostRows,
      { sampleRecurringCounts: financialSamples.recurringCounts, otherCostRecurringCounts: financialSamples.otherCostRecurringCounts },
    );
    const discount_amount = computeEffectiveDiscountRupiah(
      subtotalBeforeDiscount,
      discount_is_percentage,
      discountStr,
      discount_percentage,
    );

    let groupsBundle:
      | ReturnType<typeof validateQuotationFormGroupsForSave>
      | null = null;
    let samplesBundle: ReturnType<typeof validateQuotationFormSamplesForSave> | null = null;

    if (groupedMode) {
      groupsBundle = validateQuotationFormGroupsForSave(
        sampleGroups as QuotationFormGroupRow[],
        {
        is_contract,
        is_consultant_quote,
      });
      if (!groupsBundle.ok) {
        setSamplesSaveError(groupsBundle.message);
        setTab(sampleGroups.length ? groupTabKey(0) : 'detail');
        return;
      }
    } else {
      samplesBundle = validateQuotationFormSamplesForSave(sampleRows);
      if (!samplesBundle.ok) {
        setSamplesSaveError(samplesBundle.message);
        setTab('samples');
        return;
      }
    }
    setSamplesSaveError(null);

    let otherCostsBundle: ReturnType<typeof validateQuotationFormOtherCostsForSave> | null = null;
    if (!groupedMode) {
      otherCostsBundle = validateQuotationFormOtherCostsForSave(data.otherCostRows);
      if (!otherCostsBundle.ok) {
        setSamplesSaveError(otherCostsBundle.message);
        setTab('samples');
        return;
      }
    }

    const serverStatus = detailQ.data?.progress_status ?? progress_status;
    const submittingFromApprovedEdit =
      Boolean(detailQ.data) && quotationHasApprovedEditRequest(detailQ.data!);
    const resolvedStatus = isCreate
      ? saveIntent === 'submit'
        ? 'submitted'
        : 'draft'
      : saveIntent === 'submit' &&
          (serverStatus === 'draft' ||
            serverStatus === 'edit_requested' ||
            serverStatus === 'need_attention' ||
            quotationIsReturnedToPicStatus(serverStatus) ||
            submittingFromApprovedEdit)
        ? 'submitted'
        : progress_status;

    const commonScalars: Omit<StrapiQuotationCreateData, 'customer'> = {
      preorder,
      progress_status: resolvedStatus,
      quotation_date: quotation_date.trim() || null,
      company_branch: company_branch_row?.documentId
        ? { connect: [company_branch_row.documentId] }
        : null,
      is_contract,
      is_consultant_quote,
      ...(is_contract
        ? {
            contract_period_start_date: quotationIsoFromDatetimeLocal(contract_period_start_date),
            contract_period_end_date: quotationIsoFromDatetimeLocal(contract_period_end_date),
          }
        : {
            contract_period_start_date: null,
            contract_period_end_date: null,
          }),
      sample_pickup_method,
      discount_amount,
      is_ppn,
      term_of_payment,
      term_of_payment_date: term_of_payment
        ? quotationIsoFromDatetimeLocal(term_of_payment_date)
        : null,
      term_of_payment_note: term_of_payment ? term_of_payment_note.trim() || null : null,
      assignee_user: assignee_u ? strapiUserRelationConnect(assignee_u) : null,
      approved_by_user: approver_u ? strapiUserRelationConnect(approver_u) : null,
      ...(resolvedStatus === 'submitted'
        ? {
            request_date: detailQ.data?.request_date?.trim()
              ? detailQ.data.request_date
              : new Date().toISOString(),
            // Selesai siklus permintaan revisi saat PIC ajukan ulang.
            ...(submittingFromApprovedEdit ||
            serverStatus === 'edit_requested' ||
            serverStatus === 'need_attention'
              ? { edit_request_status: 'none' as const }
              : {}),
          }
        : {}),
    };

    try {
      if (isCreate) {
        const quotation: StrapiQuotationCreateData = {
          customer: { connect: [cust.documentId] },
          ...commonScalars,
        };

        const bundle: StrapiQuotationCreateBundleRequest = {
          quotation,
          ...(groupedMode && groupsBundle?.ok
            ? {
                sample_groups: groupsBundle.creates.map((g) => g.payload),
              }
            : {
                sample_lines: (samplesBundle?.ok ? samplesBundle.creates : []).map((c) => ({
                  jenis_pengukuran: c.payload.jenis_pengukuran,
                  parameter_uji: c.payload.parameter_uji,
                  baku_mutu: c.payload.baku_mutu,
                  sample_point_count: c.payload.sample_point_count,
                  harga: c.payload.harga,
                })),
                other_costs: (otherCostsBundle?.ok ? otherCostsBundle.creates : []).map((c) => ({
                  description: c.payload.description,
                  quantity: c.payload.quantity,
                  cost_price: c.payload.cost_price,
                })),
              }),
        };

        setPostCreateSamplesError(null);
        setPostCreateOtherCostsError(null);
        if (!groupedMode && samplesBundle?.ok && samplesBundle.updates.length > 0) {
          setPostCreateSamplesError('Data tidak konsisten: baris sampel tersimpan tidak diharapkan pada quotation baru.');
          return;
        }
        if (otherCostsBundle?.ok && otherCostsBundle.updates.length > 0) {
          setPostCreateOtherCostsError('Data tidak konsisten: biaya lain tersimpan tidak diharapkan pada quotation baru.');
          return;
        }
        const row = await createMut.mutateAsync(bundle);
        await qc.invalidateQueries({ queryKey: queryKeys.quotationDetail(jwt, row.documentId) });
        await qc.invalidateQueries({ queryKey: queryKeys.sampleInformationsFlat(jwt) });
        await qc.invalidateQueries({ queryKey: queryKeys.quotationsListPrefix(jwt) });
        await qc.invalidateQueries({ queryKey: queryKeys.workOrdersListPrefix(jwt) });
        await qc.invalidateQueries({ queryKey: [...queryKeys.strapi, 'workOrderDetail', jwt] });
        setSuccessCreate({
          quotation_number: row.quotation_number ?? '',
        });
        return;
      }

      if (isEdit && detailId) {
        const prevServerLines = detailQ.data?.sample_informations ?? [];
        const prevCostLines = detailQ.data?.quotation_other_costs ?? [];
        const sampleLinesApi = detailQ.data?.quotation_sample_lines_api ?? 'sample-informations';
        const updatePayload: StrapiQuotationUpdateData = {
          quotation_number: qNum.trim(),
          customer: { connect: [cust.documentId] },
          ...commonScalars,
        };

        try {
          // Header kontrak harus tersimpan dulu: sync-recurring-events membaca is_contract + periode dari quotation induk.
          await updateMut.mutateAsync({ documentId: detailId, data: updatePayload });
        } catch {
          return;
        }

        try {
          if (groupedMode && groupsBundle?.ok) {
            const serverGroupIds = new Set<string>();
            for (const line of prevServerLines) {
              const gid = line.quotation_group?.documentId;
              if (gid) serverGroupIds.add(gid);
            }
            const currentGroupIds = new Set(
              sampleGroups.flatMap((g) => (g.documentId ? [g.documentId] : [])),
            );
            for (const gid of serverGroupIds) {
              if (!currentGroupIds.has(gid)) {
                for (const line of prevServerLines) {
                  if (line.quotation_group?.documentId === gid) {
                    await strapiDeleteQuotationSampleInformation(jwt, line.documentId);
                  }
                }
                for (const cost of prevCostLines) {
                  if (cost.quotation_group?.documentId === gid) {
                    await strapiDeleteQuotationOtherCost(jwt, cost.documentId);
                  }
                }
                await strapiDeleteQuotationGroup(jwt, gid);
              }
            }

            for (const gCreate of groupsBundle.creates) {
              const groupRow = await strapiCreateQuotationGroup(jwt, {
                quotation: { connect: [detailId] },
                ...quotationGroupApiDataFromSavePayload(gCreate.payload),
              });
              for (const sl of gCreate.payload.sample_lines) {
                await strapiCreateQuotationSampleInformation(jwt, {
                  quotation: { connect: [detailId] },
                  quotation_group: { connect: [groupRow.documentId] },
                  jenis_pengukuran: sl.jenis_pengukuran,
                  parameter_uji: sl.parameter_uji,
                  baku_mutu: sl.baku_mutu,
                  sample_point_count: sl.sample_point_count,
                  harga: sl.harga,
                });
              }
              for (const u of gCreate.payload.sample_updates ?? []) {
                await strapiUpdateQuotationSampleInformation(jwt, u.documentId, {
                  ...u.payload,
                  quotation_group: { connect: [groupRow.documentId] },
                });
              }
              for (const oc of gCreate.payload.other_costs) {
                await strapiCreateQuotationOtherCost(jwt, {
                  quotation: { connect: [detailId] },
                  quotation_group: { connect: [groupRow.documentId] },
                  description: oc.description,
                  quantity: oc.quantity,
                  cost_price: oc.cost_price,
                });
              }
              for (const u of gCreate.payload.other_cost_updates ?? []) {
                await strapiUpdateQuotationOtherCost(jwt, u.documentId, {
                  ...u.payload,
                  quotation_group: { connect: [groupRow.documentId] },
                });
              }
              if (is_contract && gCreate.payload.recurring_type) {
                await strapiSyncQuotationGroupRecurringEvents(jwt, groupRow.documentId, {
                  quotationDocumentId: detailId,
                });
              }
            }

            for (const gUpdate of groupsBundle.updates) {
              await strapiUpdateQuotationGroup(jwt, gUpdate.documentId, {
                customer: is_consultant_quote
                  ? gUpdate.payload.customer
                    ? { connect: [gUpdate.payload.customer] }
                    : null
                  : null,
                recurring_type: is_contract ? gUpdate.payload.recurring_type ?? null : null,
                recurring_interval: is_contract ? gUpdate.payload.recurring_interval ?? 1 : null,
                recurring_count_override: is_contract
                  ? gUpdate.payload.recurring_count_override ?? null
                  : null,
                recurring_start_date: is_contract ? gUpdate.payload.recurring_start_date ?? null : null,
              });

              const prevGroupLines = prevServerLines.filter(
                (l) => l.quotation_group?.documentId === gUpdate.documentId,
              );
              const serverSampleIds = new Set(prevGroupLines.map((l) => l.documentId));
              const groupState = sampleGroups.find((g) => g.documentId === gUpdate.documentId);
              const currentSampleIds = new Set(
                groupState?.sampleRows.flatMap((r) => (r.documentId ? [r.documentId] : [])) ?? [],
              );
              for (const sid of serverSampleIds) {
                if (!currentSampleIds.has(sid)) {
                  await strapiDeleteQuotationSampleInformation(jwt, sid);
                }
              }
              for (const u of gUpdate.payload.sample_updates) {
                await strapiUpdateQuotationSampleInformation(jwt, u.documentId, u.payload);
              }
              for (const c of gUpdate.payload.sample_creates) {
                await strapiCreateQuotationSampleInformation(jwt, {
                  quotation: { connect: [detailId] },
                  quotation_group: { connect: [gUpdate.documentId] },
                  jenis_pengukuran: c.jenis_pengukuran,
                  parameter_uji: c.parameter_uji,
                  baku_mutu: c.baku_mutu,
                  sample_point_count: c.sample_point_count,
                  harga: c.harga,
                });
              }

              const prevGroupCosts = prevCostLines.filter(
                (c) => c.quotation_group?.documentId === gUpdate.documentId,
              );
              const serverCostIds = new Set(prevGroupCosts.map((c) => c.documentId));
              const currentCostIds = new Set(
                groupState?.otherCostRows.flatMap((r) => (r.documentId ? [r.documentId] : [])) ?? [],
              );
              for (const id of serverCostIds) {
                if (!currentCostIds.has(id)) {
                  await strapiDeleteQuotationOtherCost(jwt, id);
                }
              }
              for (const u of gUpdate.payload.other_cost_updates) {
                await strapiUpdateQuotationOtherCost(jwt, u.documentId, u.payload);
              }
              for (const c of gUpdate.payload.other_cost_creates) {
                await strapiCreateQuotationOtherCost(jwt, {
                  quotation: { connect: [detailId] },
                  quotation_group: { connect: [gUpdate.documentId] },
                  description: c.description,
                  quantity: c.quantity,
                  cost_price: c.cost_price,
                });
              }

              if (is_contract && gUpdate.payload.recurring_type) {
                await strapiSyncQuotationGroupRecurringEvents(jwt, gUpdate.documentId, {
                  quotationDocumentId: detailId,
                });
              }
            }
          } else if (samplesBundle?.ok) {
            const serverIds = new Set(prevServerLines.map((s) => s.documentId));
            const currentPersistedIds = new Set(
              sampleRows.flatMap((r) => (r.documentId ? [r.documentId] : [])),
            );
            for (const id of serverIds) {
              if (!currentPersistedIds.has(id)) {
                if (sampleLinesApi === 'quotation-sample-informations') {
                  await strapiDeleteQuotationSampleInformation(jwt, id);
                } else {
                  await strapiDeleteSampleInformation(jwt, id);
                }
              }
            }
            for (const u of samplesBundle.updates) {
              if (sampleLinesApi === 'quotation-sample-informations') {
                await strapiUpdateQuotationSampleInformation(jwt, u.documentId, u.payload);
              } else {
                await strapiUpdateSampleInformation(jwt, u.documentId, u.payload);
              }
            }
            for (const c of samplesBundle.creates) {
              if (sampleLinesApi === 'quotation-sample-informations') {
                await strapiCreateQuotationSampleInformation(jwt, {
                  quotation: { connect: [detailId] },
                  jenis_pengukuran: c.payload.jenis_pengukuran,
                  parameter_uji: c.payload.parameter_uji,
                  baku_mutu: c.payload.baku_mutu,
                  sample_point_count: c.payload.sample_point_count,
                  harga: c.payload.harga,
                });
              } else {
                await strapiCreateSampleInformation(jwt, {
                  quotation: { connect: [detailId] },
                  jenis_pengukuran: c.payload.jenis_pengukuran,
                  parameter_uji: c.payload.parameter_uji,
                  baku_mutu: c.payload.baku_mutu,
                  sample_point_count: c.payload.sample_point_count,
                  harga: c.payload.harga,
                });
              }
            }
          }
        } catch (sampleErr) {
          setSamplesSaveError(
            sampleErr instanceof Error ? sampleErr.message : 'Gagal menyimpan perubahan baris sampel.',
          );
          setTab(groupedMode && sampleGroups.length ? groupTabKey(0) : 'samples');
          return;
        }

        try {
          if (!groupedMode && otherCostsBundle?.ok) {
            const costServerIds = new Set(prevCostLines.map((s) => s.documentId));
            const currentCostIds = new Set(
              data.otherCostRows.flatMap((r) => (r.documentId ? [r.documentId] : [])),
            );
            for (const id of costServerIds) {
              if (!currentCostIds.has(id)) {
                await strapiDeleteQuotationOtherCost(jwt, id);
              }
            }
            for (const u of otherCostsBundle.updates) {
              await strapiUpdateQuotationOtherCost(jwt, u.documentId, u.payload);
            }
            for (const c of otherCostsBundle.creates) {
              await strapiCreateQuotationOtherCost(jwt, {
                quotation: { connect: [detailId] },
                description: c.payload.description,
                quantity: c.payload.quantity,
                cost_price: c.payload.cost_price,
              });
            }
          }
        } catch (costErr) {
          setSamplesSaveError(
            costErr instanceof Error ? costErr.message : 'Gagal menyimpan perubahan biaya lain.',
          );
          setTab(groupedMode && sampleGroups.length ? groupTabKey(0) : 'samples');
          return;
        }

        await qc.invalidateQueries({ queryKey: queryKeys.quotationDetail(jwt, detailId) });
        await qc.invalidateQueries({ queryKey: queryKeys.sampleInformationsFlat(jwt) });
        await qc.invalidateQueries({ queryKey: queryKeys.quotationsListPrefix(jwt) });
        await qc.invalidateQueries({ queryKey: queryKeys.workOrdersListPrefix(jwt) });
        await qc.invalidateQueries({ queryKey: [...queryKeys.strapi, 'workOrderDetail', jwt] });
        setSamplesSaveError(null);
        setSuccessUpdated(true);
      }
    } catch {
      /* surfaced below */
    }
  }

  function closeSuccessCreate() {
    setSuccessCreate(null);
    navigate('/quotations');
  }

  function closeSuccessUpdated() {
    setSuccessUpdated(false);
  }

  async function handleCopyQuotationFromView(): Promise<void> {
    if (!jwt || !detailQ.data?.documentId) return;
    setCopyError(null);
    try {
      const copied = await copyMut.mutateAsync(detailQ.data.documentId);
      navigate(`/quotation/${encodeURIComponent(copied.documentId)}/edit`);
    } catch (err) {
      setCopyError(err instanceof Error ? err.message : 'Gagal copy quotation.');
    }
  }

  const mutationError = isCreate ? createMut.error : updateMut.error;

  if (!isCreate && (!detailId || detailId.trim() === '')) {
    return (
      <PageShell title="Detail quotation">
        <Alert severity="warning">ID quotation tidak valid.</Alert>
        <Button component={RouterLink} to="/quotations" sx={{ mt: 2 }}>
          Kembali ke daftar
        </Button>
      </PageShell>
    );
  }

  if (!isCreate && !isEdit && !isView) {
    return (
      <PageShell title="Quotation">
        <Alert severity="warning">Rute tidak dikenal.</Alert>
        <Button component={RouterLink} to="/quotations" sx={{ mt: 2 }}>
          Kembali ke daftar
        </Button>
      </PageShell>
    );
  }

  if (isCreate && !authLoading && !canCreateQuotation(workflowUser ?? user)) {
    return <Navigate to="/quotations" replace />;
  }

  if (
    isEdit &&
    !authLoading &&
    detailId &&
    (isAdminKeuangan(workflowUser) || isAdminKeuangan(user))
  ) {
    return <Navigate to={`/quotations/${encodeURIComponent(detailId)}`} replace />;
  }

  if ((isEdit || isView) && detailQ.isPending) {
    return (
      <PageShell fill title={pageHeading}>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      </PageShell>
    );
  }

  if ((isEdit || isView) && detailQ.isError) {
    return (
      <PageShell title={pageHeading}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {detailQ.error instanceof Error ? detailQ.error.message : 'Gagal memuat quotation.'}
        </Alert>
        <Button component={RouterLink} to="/quotations">
          Kembali ke daftar
        </Button>
      </PageShell>
    );
  }

  return (
    <PageShell
      fill
      title={pageHeading}
      extras={
        <Tabs
          value={resolvedTab}
          onChange={(e, v: QuotationFormTabKey) => void handleTabsChange(e, v)}
          sx={PAGE_SHELL_SCROLLABLE_TABS_SX}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab label="Detail" value="detail" data-testid="quotation-form-tab-detail" />
          {usesGroupedSamples
            ? values.sampleGroups.map((g, i) => (
                <Tab
                  key={g.key}
                  label={`Sampel (${i + 1})`}
                  value={groupTabKey(i)}
                  data-testid={`quotation-form-tab-sample-group-${i + 1}`}
                />
              ))
            : (
                <Tab label="Sampel" value="samples" data-testid="quotation-form-tab-samples" />
              )}
          {showNotesTab ? (
            <Tab label="Catatan quotation" value="notes" data-testid="quotation-form-tab-notes" />
          ) : null}
          {(isEdit || isView) && showLogsTab ? (
            <Tab label="Quotation logs" value="logs" data-testid="quotation-form-tab-logs" />
          ) : null}
        </Tabs>
      }
    >
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <CollapsibleKeteranganSplit
          toggleTestId="quotation-info"
          keterangan={
            isCreate ||
            (detailQ.data && quotationEditRequestStatus(detailQ.data) === 'pending') ||
            (detailQ.data &&
              quotationHasApprovedEditRequest(detailQ.data) &&
              detailQ.data.progress_status !== 'edit_requested' &&
              detailQ.data.progress_status !== 'need_attention') ||
            (detailQ.data && canShowPicApproveRevision(detailQ.data, workflowUser)) ||
            (isEdit && formReadOnly) ||
            (mutationError && !isView) ||
            samplesSaveError ||
            copyError ||
            postCreateSamplesError ||
            postCreateOtherCostsError ? (
              <Stack spacing={1}>
                {isCreate ? (
                  <Typography variant="body2" color="text.secondary">
                    Nomor quotation (format QUO.DDMMYY.##### menurut zona WIB) akan dibuat otomatis saat
                    Anda menyimpan. Gunakan <strong>Simpan draft</strong> untuk menyimpan sementara, atau{' '}
                    <strong>Ajukan</strong> agar SVP marketing dapat meninjau. Nomor GL work order dibuat
                    setelah pelanggan menyetujui quotation.
                  </Typography>
                ) : null}
                {detailQ.data && quotationEditRequestStatus(detailQ.data) === 'pending' ? (
                  <Alert severity="warning" data-testid="quotation-edit-request-pending-alert">
                    Permintaan revisi menunggu review SPV. Status quotation utama tetap{' '}
                    <strong>{detailQ.data.progress_status}</strong>
                    {(detailQ.data.remark_edit ?? '').trim()
                      ? ` — catatan PIC: ${(detailQ.data.remark_edit ?? '').trim()}`
                      : ''}
                    .
                  </Alert>
                ) : null}
                {detailQ.data &&
                quotationHasApprovedEditRequest(detailQ.data) &&
                detailQ.data.progress_status !== 'edit_requested' &&
                detailQ.data.progress_status !== 'need_attention' ? (
                  <Alert severity="info" data-testid="quotation-edit-request-approved-alert">
                    Permintaan revisi telah disetujui SPV. PIC penanggung jawab dapat mengubah quotation
                    lalu mengajukan ulang.
                  </Alert>
                ) : null}
                {detailQ.data && canShowPicApproveRevision(detailQ.data, workflowUser) ? (
                  <Alert severity="info" data-testid="quotation-pic-approve-revision-alert">
                    Quotation menunggu persetujuan PIC. Setelah Setuju, alur lanjut ke SPV marketing
                    {((detailQ.data.remark_edit ?? '').trim()
                      ? ` — ${(detailQ.data.remark_edit ?? '').trim()}`
                      : '')}
                    .
                  </Alert>
                ) : null}
                {isEdit && formReadOnly ? (
                  <Alert severity="info">
                    {quotationIsViewOnly(detailQ.data!)
                      ? 'Quotation dikunci — hanya tampilan.'
                      : quotationEditRequestStatus(detailQ.data!) === 'pending'
                        ? 'Menunggu SPV menyetujui permintaan revisi — quotation belum dapat diubah oleh PIC.'
                        : detailQ.data?.work_order_created
                          ? 'Quotation sudah memiliki work order. Gunakan Minta revisi jika perlu mengubah data.'
                          : detailQ.data?.progress_status === 'submitted' ||
                              detailQ.data?.progress_status === 'approved' ||
                              detailQ.data?.progress_status === 'need_manajer_approval' ||
                              detailQ.data?.progress_status === 'manajer_approved'
                            ? 'Quotation sudah diajukan — hanya dapat dilihat.'
                            : detailQ.data?.progress_status === 'edit_requested' ||
                                detailQ.data?.progress_status === 'need_attention' ||
                                quotationHasApprovedEditRequest(detailQ.data!)
                              ? 'Permintaan revisi — hanya PIC marketing penanggung jawab yang dapat mengubah quotation ini.'
                              : quotationIsReturnedToPicStatus(detailQ.data?.progress_status)
                                ? detailQ.data?.progress_status === 'rejected_by_manajer'
                                  ? 'Quotation ditolak manajer — perbaiki lalu ajukan kembali.'
                                  : 'Quotation ditolak — perbaiki lalu ajukan kembali.'
                                : 'Anda tidak dapat mengubah quotation ini.'}
                  </Alert>
                ) : null}
                {mutationError && !isView ? (
                  <Alert severity="error">
                    {mutationError instanceof Error ? mutationError.message : 'Gagal menyimpan.'}
                  </Alert>
                ) : null}
                {samplesSaveError ? (
                  <Alert severity="error" onClose={() => setSamplesSaveError(null)}>
                    {samplesSaveError}
                  </Alert>
                ) : null}
                {copyError ? (
                  <Alert severity="error" onClose={() => setCopyError(null)}>
                    {copyError}
                  </Alert>
                ) : null}
                {postCreateSamplesError ? (
                  <Alert severity="warning" onClose={() => setPostCreateSamplesError(null)}>
                    Quotation sudah dibuat, tetapi menyimpan baris sampel gagal: {postCreateSamplesError}
                  </Alert>
                ) : null}
                {postCreateOtherCostsError ? (
                  <Alert severity="warning" onClose={() => setPostCreateOtherCostsError(null)}>
                    Quotation sudah dibuat, tetapi menyimpan biaya lain gagal: {postCreateOtherCostsError}
                  </Alert>
                ) : null}
              </Stack>
            ) : null
          }
        >
          {resolvedTab === 'detail' ? (
            <QuotationFormDetailTabScreen
              jwt={jwt}
              quotation_number={values.quotation_number}
              quotation_date={values.quotation_date}
              company_branch={values.company_branch}
              companyBranchError={detailErrors.company_branch}
              customer={values.customer}
              preorder={values.preorder}
              progress_status={values.progress_status}
              edit_request_status={detailQ.data?.edit_request_status ?? null}
              formReadOnly={formReadOnly}
              hideStatusField={isCreate || isMarketingPic(user) || isView}
              is_contract={values.is_contract}
              is_consultant_quote={values.is_consultant_quote}
              contract_period_start_date={values.contract_period_start_date}
              contract_period_end_date={values.contract_period_end_date}
              contractPeriodStartDateError={detailErrors.contract_period_start_date}
              contractPeriodEndDateError={detailErrors.contract_period_end_date}
              onAddSampleGroup={handleAddSampleGroup}
              onPatchContractFlags={patchContractFlags}
              sample_pickup_method={values.sample_pickup_method}
              assignee_user={values.assignee_user}
              approved_by_user={values.approved_by_user}
              term_of_payment={values.term_of_payment}
              term_of_payment_date={values.term_of_payment_date}
              term_of_payment_note={values.term_of_payment_note}
              termOfPaymentDateError={detailErrors.term_of_payment_date}
              onPatch={patchValues}
              customerError={detailErrors.customer}
              quotationNumberError={detailErrors.quotation_number}
              quotationNumberDisabled={isEdit || isView}
              quotationNumberFreeze={savePending && !isEdit}
              hideQuotationNumberInput={isCreate}
              customerSelectDisabled={savePending}
              quotationNumberTestId={isView ? 'quotation-detail-number' : 'quotation-form-number'}
            />
          ) : null}
          {!usesGroupedSamples && resolvedTab === 'samples' ? (
            <QuotationFormSamplesTabScreen
              disabled={savePending || formReadOnly}
              readOnly={formReadOnly}
              sampleRows={values.sampleRows}
              onSampleRowsChange={(sampleRows) => patchValues({ sampleRows })}
              discount_amount={values.discount_amount}
              discount_is_percentage={values.discount_is_percentage}
              discount_percentage={values.discount_percentage}
              is_ppn={values.is_ppn}
              otherCostRows={values.otherCostRows}
              onFinancialPatch={patchValues}
            />
          ) : null}
          {usesGroupedSamples
            ? values.sampleGroups.map((group, groupIndex) =>
                resolvedTab === groupTabKey(groupIndex) ? (
                  <QuotationFormSampleGroupTabScreen
                    key={group.key}
                    jwt={jwt}
                    groupIndex={groupIndex}
                    group={group}
                    is_contract={values.is_contract}
                    is_consultant_quote={values.is_consultant_quote}
                    contract_period_start_date={values.contract_period_start_date}
                    contract_period_end_date={values.contract_period_end_date}
                    contractPeriodStartDateError={detailErrors.contract_period_start_date}
                    contractPeriodEndDateError={detailErrors.contract_period_end_date}
                    onContractPeriodChange={patchValues}
                    disabled={savePending || formReadOnly}
                    readOnly={formReadOnly}
                    showFinancialSummary={groupIndex === 0}
                    canRemoveGroup={values.sampleGroups.length > 0}
                    onGroupChange={(nextGroup) => {
                      setValues((v) => ({
                        ...v,
                        sampleGroups: v.sampleGroups.map((g, i) =>
                          i === groupIndex ? nextGroup : g,
                        ),
                      }));
                    }}
                    onRemoveGroup={() => handleRemoveSampleGroup(groupIndex)}
                    discount_amount={values.discount_amount}
                    discount_is_percentage={values.discount_is_percentage}
                    discount_percentage={values.discount_percentage}
                    is_ppn={values.is_ppn}
                    onFinancialPatch={patchValues}
                    financialSampleRows={financialBreakdown.rows}
                    financialSampleRecurringCounts={financialBreakdown.recurringCounts}
                    groupSampleSubtotals={financialBreakdown.groupSubtotals}
                    groupOtherCostSubtotals={financialBreakdown.groupOtherCostSubtotals}
                    allOtherCostRowsForGrandTotal={financialBreakdown.allOtherCostRows}
                    allOtherCostRecurringCounts={financialBreakdown.otherCostRecurringCounts}
                  />
                ) : null,
              )
            : null}
          {(isEdit || isView) && showNotesTab && resolvedTab === 'notes' ? (
            <QuotationFormCatatanQuotationTabScreen
              remark_edit={detailQ.data?.remark_edit}
              remark_edit_review={detailQ.data?.remark_edit_review}
              remark_approval={detailQ.data?.remark_approval}
            />
          ) : null}
          {(isEdit || isView) && showLogsTab && detailId && resolvedTab === 'logs' ? (
            <QuotationLogsTabScreen quotationDocumentId={detailId} readOnly={formReadOnly} />
          ) : null}
        </CollapsibleKeteranganSplit>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            py: 1,
            flexShrink: 0,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            alignItems: 'center',
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => navigate('/quotations')}
            disabled={createMut.isPending || updateMut.isPending}
            data-testid={isView ? 'quotation-view-close' : undefined}
          >
            {isView ? 'Tutup' : 'Batal'}
          </Button>
          {isView && detailQ.data && detailId ? (
            <QuotationWorkflowActions
              jwt={jwt}
              row={detailQ.data}
              user={workflowUser ?? null}
              variant="footer"
              onCreateWorkOrder={() =>
                navigate(`/quotation/${encodeURIComponent(detailId)}/work-order/new`)
              }
              onViewSamplingSchedules={async () => {
                const row = detailQ.data!;
                if (!jwt) {
                  navigate('/sampling/schedules');
                  return;
                }
                const path = await resolveSamplingSchedulePath(jwt, row);
                navigate(path);
              }}
              onUpdated={() => void detailQ.refetch()}
              accessCodes={menuQ.data?.access_codes}
            />
          ) : null}
          {isView && detailQ.data && canPicEditQuotation(detailQ.data, workflowUser) ? (
            <Button
              variant="contained"
              onClick={() => navigate(`/quotation/${encodeURIComponent(detailId!)}/edit`)}
              data-testid="quotation-view-edit"
            >
              Ubah quotation
            </Button>
          ) : null}
          {isView && detailQ.data && canCopyQuotation(detailQ.data, workflowUser) ? (
            <Button
              variant="outlined"
              onClick={() => void handleCopyQuotationFromView()}
              disabled={copyMut.isPending}
              data-testid="quotation-view-copy"
            >
              Copy quotation
            </Button>
          ) : null}
          {isCreate ? (
            <>
              <Button
                variant="outlined"
                disabled={saveDisabled || resolvedTab === 'logs'}
                onClick={() => void submitValidated('draft')}
                data-testid="quotation-form-save-draft"
              >
                Simpan draft
              </Button>
              <Button
                variant="contained"
                disabled={saveDisabled || resolvedTab === 'logs'}
                onClick={() => void submitValidated('submit')}
                data-testid="quotation-form-submit"
              >
                Ajukan
              </Button>
            </>
          ) : canEditForm ? (
            showSubmitOnEdit ? (
              <>
                <Button
                  variant="outlined"
                  disabled={saveDisabled || resolvedTab === 'logs'}
                  onClick={() => void submitValidated('draft')}
                  data-testid="quotation-form-save-draft"
                >
                  {detailQ.data?.progress_status === 'edit_requested' ||
                  detailQ.data?.progress_status === 'need_attention' ||
                  quotationHasApprovedEditRequest(detailQ.data!) ||
                  quotationIsReturnedToPicStatus(detailQ.data?.progress_status)
                    ? 'Simpan'
                    : 'Simpan draft'}
                </Button>
                <Button
                  variant="contained"
                  disabled={saveDisabled || resolvedTab === 'logs'}
                  onClick={() => void submitValidated('submit')}
                  data-testid="quotation-form-submit"
                >
                  Ajukan
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                disabled={saveDisabled || resolvedTab === 'logs'}
                onClick={() => void submitValidated('draft')}
                data-testid="quotation-form-save"
              >
                Simpan
              </Button>
            )
          ) : null}
        </Box>
      </Box>

      <Dialog open={Boolean(successCreate)} onClose={closeSuccessCreate} maxWidth="xs" fullWidth>
        {successCreate ? (
          <>
            <DialogTitle>Quotation dibuat</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                Nomor quotation:{' '}
                <strong data-testid="quotation-create-success-number">{successCreate.quotation_number}</strong>
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button variant="contained" onClick={closeSuccessCreate}>
                OK
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Dialog open={successUpdated} onClose={closeSuccessUpdated} maxWidth="xs" fullWidth>
        <DialogTitle>Tersimpan</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Perubahan quotation telah disimpan.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button variant="contained" onClick={closeSuccessUpdated}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
